import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, ArrowRight, Mail, Instagram, MapPin } from 'lucide-react';
import FAQS from '@/data/faqs';

// Footer shows a curated subset; full list lives on /faq
const FOOTER_FAQS = [
  FAQS[0], // How long does a custom order take?
  FAQS[1], // Can I customize colors and design details?
  FAQS[2], // Do you ship across Canada?
  FAQS[3], // Are all pieces handmade?
  FAQS[4], // Can I order personalized gifts?
  FAQS[5], // Can I request a completely custom design?
  FAQS[FAQS.length - 1], // How do I contact you?
];

// Compact accordion item for the footer "Quick Questions" column.
const FooterFAQItem = ({ q, a, isOpen, onToggle, idx }) => (
  <div
    className={`rounded-xl border transition-all duration-400 overflow-hidden ${
      isOpen
        ? 'border-[#D4AF5A]/50 bg-white/70'
        : 'border-[#0B1F26]/8 bg-white/40 hover:bg-white/60 hover:border-[#0B1F26]/18'
    }`}
    data-testid={`footer-faq-item-${idx}`}
  >
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={isOpen}
      className="w-full flex items-center justify-between gap-3 text-left px-4 py-3 focus:outline-none"
      data-testid={`footer-faq-question-${idx}`}
    >
      <span className="text-[13px] leading-snug text-[#1A1A1A]">{q}</span>
      <Plus
        size={14}
        strokeWidth={1.8}
        className={`flex-shrink-0 text-[#0B1F26] transition-transform duration-400 ${
          isOpen ? 'rotate-[135deg] text-[#D4AF5A]' : ''
        }`}
      />
    </button>
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="overflow-hidden"
        >
          <div className="px-4 pb-3 -mt-1">
            <div
              className="h-[1px] w-8 mb-2"
              style={{ background: 'linear-gradient(90deg, #D4AF5A 0%, transparent 100%)' }}
            />
            <p className="text-[12.5px] leading-relaxed text-[#4A4A4A]">{a}</p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

export const Footer = () => {
  const [openFaqIdx, setOpenFaqIdx] = useState(-1);

  return (
    <footer
      className="border-t border-black/5 relative overflow-hidden"
      style={{
        background:
          'linear-gradient(180deg, #F5F2EC 0%, #ECF0F0 60%, #DCE5E7 100%)',
      }}
      data-testid="site-footer"
    >
      {/* Decorative top hairline */}
      <div
        aria-hidden
        className="absolute top-0 left-0 right-0 h-[1px] opacity-50"
        style={{
          background:
            'linear-gradient(90deg, transparent 0%, rgba(212,175,90,0.6) 50%, transparent 100%)',
        }}
      />

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 pt-20 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12">
          {/* Brand + Social */}
          <div className="md:col-span-4">
            <h3
              className="text-2xl mb-3"
              style={{ fontFamily: 'Playfair Display, serif', fontWeight: 500, color: '#0B1F26' }}
            >
              Navs Art Studio
            </h3>
            <p className="text-[#1E4A55] text-sm leading-relaxed mb-3 italic">
              Where Texture Meets Emotion
            </p>
            <p className="text-[#4A4A4A] text-sm leading-relaxed mb-6">
              Handcrafted, one-of-a-kind artworks rooted in elegance and individuality.
            </p>
            <div className="flex items-center gap-3" data-testid="footer-social">
              <a
                href="https://www.instagram.com/navs.art.studio/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                data-testid="footer-social-instagram"
                className="w-9 h-9 rounded-full flex items-center justify-center bg-white/60 border border-[#0B1F26]/10 text-[#0B1F26] hover:bg-[#0B1F26] hover:text-[#E8D9B5] hover:border-[#0B1F26] transition-all duration-300"
              >
                <Instagram size={15} />
              </a>
              <a
                href="mailto:hello@navsartstudio.com"
                aria-label="Email us"
                data-testid="footer-social-email"
                className="w-9 h-9 rounded-full flex items-center justify-center bg-white/60 border border-[#0B1F26]/10 text-[#0B1F26] hover:bg-[#0B1F26] hover:text-[#E8D9B5] hover:border-[#0B1F26] transition-all duration-300"
              >
                <Mail size={15} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="md:col-span-2">
            <h4 className="text-[11px] uppercase tracking-[0.28em] mb-5 text-[#0B1F26]">Explore</h4>
            <div className="space-y-2.5">
              <Link to="/featured" className="block text-sm text-[#4A4A4A] hover:text-[#0B1F26] transition-colors">
                Featured
              </Link>
              <Link to="/ocean" className="block text-sm text-[#4A4A4A] hover:text-[#0B1F26] transition-colors">
                Ocean Series
              </Link>
              <Link to="/sports-cars" className="block text-sm text-[#4A4A4A] hover:text-[#0B1F26] transition-colors">
                Sports &amp; Cars
              </Link>
              <Link to="/coasters" className="block text-sm text-[#4A4A4A] hover:text-[#0B1F26] transition-colors">
                Coasters
              </Link>
              <Link to="/story" className="block text-sm text-[#4A4A4A] hover:text-[#0B1F26] transition-colors">
                Our Story
              </Link>
              <Link to="/blog" className="block text-sm text-[#4A4A4A] hover:text-[#0B1F26] transition-colors">
                Blog
              </Link>
              <Link to="/contact" className="block text-sm text-[#4A4A4A] hover:text-[#0B1F26] transition-colors">
                Contact
              </Link>
            </div>
          </div>

          {/* Quick Questions (FAQ accordion) */}
          <div className="md:col-span-6" data-testid="footer-faq">
            <div className="flex items-baseline justify-between mb-5">
              <h4 className="text-[11px] uppercase tracking-[0.28em] text-[#0B1F26]">
                Quick Questions
              </h4>
              <Link
                to="/faq"
                className="text-[11px] uppercase tracking-[0.18em] text-[#0B1F26] hover:text-[#D4AF5A] transition-colors flex items-center gap-1"
                data-testid="footer-faq-view-all"
              >
                View all
                <ArrowRight size={11} />
              </Link>
            </div>
            <div className="space-y-2">
              {FOOTER_FAQS.map((item, i) => (
                <FooterFAQItem
                  key={item.q}
                  idx={i}
                  q={item.q}
                  a={item.a}
                  isOpen={openFaqIdx === i}
                  onToggle={() => setOpenFaqIdx(openFaqIdx === i ? -1 : i)}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.7, ease: 'easeOut' }}
          className="mt-16 rounded-2xl px-6 sm:px-10 py-8 sm:py-10 relative overflow-hidden"
          style={{
            background:
              'linear-gradient(135deg, #0B1F26 0%, #143942 55%, #1E4A55 100%)',
          }}
          data-testid="footer-cta"
        >
          <div
            aria-hidden
            className="absolute -top-1/2 -right-1/4 w-[50%] h-[200%] opacity-[0.06] pointer-events-none"
            style={{
              background:
                'radial-gradient(ellipse at center, #D4AF5A 0%, transparent 60%)',
            }}
          />
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 relative">
            <div>
              <p
                className="text-[10px] uppercase tracking-[0.5em] mb-2"
                style={{ color: '#D4AF5A' }}
              >
                We&apos;re here to help
              </p>
              <h3
                className="text-white text-2xl sm:text-3xl leading-tight"
                style={{ fontFamily: 'Playfair Display, serif', fontWeight: 500 }}
              >
                Still have <em className="italic" style={{ color: '#E8D9B5' }}>questions?</em>
              </h3>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <Link to="/faq" data-testid="footer-cta-faq">
                <button
                  className="group w-full sm:w-auto rounded-full px-7 py-3 text-[11px] tracking-[0.18em] uppercase font-medium text-[#0B1F26] transition-all duration-500 hover:shadow-[0_10px_30px_rgba(212,175,90,0.4)] hover:-translate-y-0.5"
                  style={{
                    background:
                      'linear-gradient(135deg, #F1E0B0 0%, #D4AF5A 55%, #B8923E 100%)',
                  }}
                >
                  <span className="flex items-center justify-center gap-2">
                    View Full FAQ
                    <ArrowRight size={13} className="transition-transform group-hover:translate-x-1" />
                  </span>
                </button>
              </Link>
              <Link to="/contact" data-testid="footer-cta-contact">
                <button className="group w-full sm:w-auto rounded-full px-7 py-3 text-[11px] tracking-[0.18em] uppercase font-medium text-white border border-white/30 backdrop-blur-md bg-white/5 hover:bg-white/15 hover:border-white/70 transition-all duration-500 hover:-translate-y-0.5">
                  <span className="flex items-center justify-center gap-2">
                    <Mail size={13} />
                    Contact Us
                  </span>
                </button>
              </Link>
            </div>
          </div>
        </motion.div>

        <div className="mt-12 pt-6 border-t border-[#0B1F26]/10 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-[#4A4A4A] flex items-center gap-2">
            <MapPin size={12} className="text-[#D4AF5A]" />
            Handcrafted in Vancouver, Canada
          </p>
          <p className="text-xs text-[#888888]">
            © {new Date().getFullYear()} Navs Art Studio. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};
